import { OrganizationTaskTemplateSortBy } from "@common/schemas/organization-task-template.schema";
import { OrganizationTaskTemplatesViewColumn } from "@common/types/views/organizationTaskTemplatesView";
import { ViewConfig } from "@lib/views";
import type { TaskTypeOption } from "../OrganizationCloseTemplates/closeTemplateTasksDrawer.constants";
import {
    CloseTemplateTaskColumn,
    CloseTemplateTaskRow,
    closeTemplateTasksViewConfig,
} from "../OrganizationCloseTemplates/closeTemplateTasksViewConfig";
import { OrganizationTaskTemplateActionsCell } from "./cells/OrganizationTaskTemplateActionsCell";
import { OrganizationTaskTemplateClientCell } from "./cells/OrganizationTaskTemplateClientCell";
import { OrganizationTaskTemplateCreatedCell } from "./cells/OrganizationTaskTemplateCreatedCell";
import {
    OrganizationTaskTemplateCreatedByCell,
    OrganizationTaskTemplateUpdatedByCell,
} from "./cells/OrganizationTaskTemplateMemberCell";
import { OrganizationTaskTemplateServiceTemplatesCell } from "./cells/OrganizationTaskTemplateServiceTemplatesCell";
import { OrganizationTaskTemplateNameCell } from "./cells/OrganizationTaskTemplateNameCell";
import { OrganizationTaskTemplateUpdatedCell } from "./cells/OrganizationTaskTemplateUpdatedCell";
import { withKickDefaultPlaceholder } from "./cells/withKickDefaultPlaceholder";
import {
    columnSort,
    OrganizationTaskTemplateSortValue,
} from "./organizationTaskTemplatesSort";

export type OrganizationTaskTemplateRow = CloseTemplateTaskRow & {
    kickDefaultOption?: TaskTypeOption;
};

const reuseTaskColumn = (
    column: CloseTemplateTaskColumn,
    sortBy: OrganizationTaskTemplateSortBy,
) => ({
    ...closeTemplateTasksViewConfig.columns[column],
    sort: columnSort(sortBy),
});

const reuseEditableTaskColumn = (
    column: CloseTemplateTaskColumn,
    sortBy: OrganizationTaskTemplateSortBy,
) => {
    const { cell, ...reusedColumn } = reuseTaskColumn(column, sortBy);
    return {
        ...reusedColumn,
        cell: cell && withKickDefaultPlaceholder(cell),
    };
};

export const organizationTaskTemplatesViewConfig: ViewConfig<
    OrganizationTaskTemplateRow,
    OrganizationTaskTemplatesViewColumn,
    OrganizationTaskTemplateSortValue
> = {
    columns: {
        [OrganizationTaskTemplatesViewColumn.NAME]: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            cell: OrganizationTaskTemplateNameCell,
            sort: columnSort(OrganizationTaskTemplateSortBy.TITLE),
            immutable: true,
            minSize: 240,
            maxSize: 800,
        },
        [OrganizationTaskTemplatesViewColumn.TASK_TYPE]: reuseTaskColumn(
            CloseTemplateTaskColumn.TASK_TYPE,
            OrganizationTaskTemplateSortBy.TYPE,
        ),
        [OrganizationTaskTemplatesViewColumn.ASSIGNEE]: reuseEditableTaskColumn(
            CloseTemplateTaskColumn.ASSIGNEE,
            OrganizationTaskTemplateSortBy.ASSIGNEE_ROLE,
        ),
        [OrganizationTaskTemplatesViewColumn.CLOSE_STAGE]:
            reuseEditableTaskColumn(
                CloseTemplateTaskColumn.CLOSE_STAGE,
                OrganizationTaskTemplateSortBy.CLOSE_STAGE,
            ),
        [OrganizationTaskTemplatesViewColumn.DEADLINE]: reuseEditableTaskColumn(
            CloseTemplateTaskColumn.DEADLINE,
            OrganizationTaskTemplateSortBy.DEADLINE,
        ),
        [OrganizationTaskTemplatesViewColumn.ESTIMATE]: reuseEditableTaskColumn(
            CloseTemplateTaskColumn.ESTIMATE,
            OrganizationTaskTemplateSortBy.ESTIMATE,
        ),
        [OrganizationTaskTemplatesViewColumn.CLIENT]: {
            accessorKey: "id",
            name: "Client",
            header: "Client",
            cell: OrganizationTaskTemplateClientCell,
            minSize: 160,
            maxSize: 300,
        },
        [OrganizationTaskTemplatesViewColumn.SERVICE_TEMPLATES]: {
            accessorKey: "id",
            name: "Service template",
            header: "Service template",
            cell: withKickDefaultPlaceholder(
                OrganizationTaskTemplateServiceTemplatesCell,
            ),
            align: "right",
            minSize: 140,
            maxSize: 200,
        },
        [OrganizationTaskTemplatesViewColumn.CREATED_BY]: {
            accessorKey: "id",
            name: "Created by",
            header: "Created by",
            cell: OrganizationTaskTemplateCreatedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [OrganizationTaskTemplatesViewColumn.CREATED]: {
            accessorKey: "id",
            name: "Created on",
            header: "Created on",
            cell: OrganizationTaskTemplateCreatedCell,
            sort: columnSort(OrganizationTaskTemplateSortBy.CREATED_AT),
            minSize: 140,
            maxSize: 180,
        },
        [OrganizationTaskTemplatesViewColumn.UPDATED_BY]: {
            accessorKey: "id",
            name: "Updated by",
            header: "Updated by",
            cell: OrganizationTaskTemplateUpdatedByCell,
            minSize: 160,
            maxSize: 240,
        },
        [OrganizationTaskTemplatesViewColumn.UPDATED]: {
            accessorKey: "id",
            name: "Updated on",
            header: "Updated on",
            cell: OrganizationTaskTemplateUpdatedCell,
            sort: columnSort(OrganizationTaskTemplateSortBy.UPDATED_AT),
            minSize: 140,
            maxSize: 180,
        },
        [OrganizationTaskTemplatesViewColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: OrganizationTaskTemplateActionsCell,
            minSize: 120,
            maxSize: 120,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};
